import { mat4, type Mat4 } from '../math/mat4'
import { vec3, type Vec3 } from '../math/vec3'
import { pose as poseOps, type Pose } from '../math/pose'
import type { CameraModel } from '../camera/model'
import { fitLandmarkDepth, landmarkToMetric, type LandmarkDepthModel } from '../camera/metric-landmarks'
import type { FaceFrame } from '../tracking/types'
import { FACE_LM } from '../tracking/landmarks'
import type { AnchorSpec } from '../product/schema'
import { FACE_ANCHORS } from './face-anchors'
import { solveFaceAnchor, type FaceAnchorResult } from './face-anchor'

/** Geometry the glasses renderer needs beyond the pose (all in the *anchor's local frame*, cm). */
export interface GlassesFit {
  /**
   * Temple tip targets relative to the glasses anchor: the tragus landmark lifted to the top of the
   * ear junction (where the temple actually rests), then extended past it behind the ear.
   */
  earLeft: Vec3
  earRight: Vec3
  /** Straight-line temple length from a nominal hinge to each ear. */
  templeLengthLeft: number
  templeLengthRight: number
  /** Distance between the ears (cm) — frame width hint. */
  headWidth: number
  /** Inter-pupillary distance estimate (cm, metric landmarks). */
  ipd: number
}

export interface GlassesSolveResult extends FaceAnchorResult {
  fit: GlassesFit
}

export interface GlassesSolverOptions {
  /** Hinge x-offset from the bridge (half the frame width) used for temple length, cm. */
  hingeHalfWidth: number
  /**
   * The ear landmarks (234/454) sit at the mesh edge in front of the ear; real temples continue
   * past them to hook behind the ear. Ear points are pushed back along the temple by this much (cm).
   */
  templeExtension: number
  /**
   * The ear landmarks track the *tragus* (front of the ear canal) — ~2 cm below where a temple
   * actually rests (the top of the ear junction). The target is raised by this much (cm, head-up axis)
   * so temples run nearly horizontal instead of sloping down across the cheek.
   */
  templeRestLift: number
  refine2D: boolean
}

export const GLASSES_SOLVER_DEFAULTS: GlassesSolverOptions = {
  hingeHalfWidth: 6.4,
  templeExtension: 4.5,
  templeRestLift: 2.2,
  refine2D: true,
}

const _inv: Mat4 = mat4.create()
const _p: Vec3 = [0, 0, 0]
const _hinge: Vec3 = [0, 0, 0]
const _tmp: Vec3 = [0, 0, 0]
const _depth: LandmarkDepthModel = { depthRef: 0, zScale: 1 }

function toAnchorLocal(out: Vec3, camPoint: Vec3, anchorInv: Mat4): Vec3 {
  return mat4.transformPoint(out, anchorInv, camPoint)
}

/**
 * Glasses solver: bridge-anchored face pose + ear points for temple alignment.
 * The anchor frame's origin is the nose bridge; +X toward the subject's left, +Z out of the face.
 */
export function solveGlasses(
  frame: FaceFrame,
  camera: CameraModel,
  spec: AnchorSpec,
  options: Partial<GlassesSolverOptions> = {},
  out?: GlassesSolveResult,
): GlassesSolveResult {
  const opts = { ...GLASSES_SOLVER_DEFAULTS, ...options }
  const res: GlassesSolveResult =
    out ??
    ({
      pose: poseOps.create(),
      local: poseOps.create(),
      head: poseOps.create(),
      fit: { earLeft: [0, 0, 0], earRight: [0, 0, 0], templeLengthLeft: 0, templeLengthRight: 0, headWidth: 0, ipd: 0 },
    } as GlassesSolveResult)

  solveFaceAnchor({ frame, camera, spec: { ...spec, origin: spec.origin ?? 'nose_bridge' }, refine2D: opts.refine2D }, res)

  // anchor → camera matrix (with unit scale so the fit values stay in cm)
  const anchorMat = mat4.compose(mat4.create(), res.pose.position, res.pose.rotation, 1)
  const inv = mat4.invert(_inv, anchorMat)
  const depthRef: LandmarkDepthModel = fitLandmarkDepth(frame.landmarks, frame.headMatrix, camera, _depth)

  const earFor = (lmIndex: number, canonical: Vec3, outV: Vec3): void => {
    if (lmIndex * 3 + 2 < frame.landmarks.length) {
      // metric ear from the observed landmark: exact in x/y, depth from head matrix + landmark z
      landmarkToMetric(_p, frame.landmarks, lmIndex, camera, depthRef)
      // clamp depth to the head-matrix ear depth (landmark z at the mesh edge is noisy)
      mat4.transformPoint(_tmp, frame.headMatrix, canonical)
      _p[2] = _tmp[2]
    } else {
      mat4.transformPoint(_p, frame.headMatrix, canonical)
    }
    if (inv) toAnchorLocal(outV, _p, inv)
    else vec3.copy(outV, _p)
    // tragus → temple rest: lift to the top of the ear junction (anchor frame shares the head's up axis)
    outV[1] += opts.templeRestLift
  }

  earFor(FACE_LM.LEFT_EAR, FACE_ANCHORS.ear_left.point, res.fit.earLeft)
  earFor(FACE_LM.RIGHT_EAR, FACE_ANCHORS.ear_right.point, res.fit.earRight)

  // extend past the mesh-edge ear landmark along the hinge→ear direction
  const extend = (hingeX: number, ear: Vec3): number => {
    vec3.set(_hinge, hingeX, 0, 0)
    vec3.sub(_tmp, ear, _hinge)
    const d = vec3.length(_tmp)
    if (d > 1e-3 && opts.templeExtension > 0) vec3.scaleAndAdd(ear, ear, _tmp, opts.templeExtension / d)
    return d + opts.templeExtension
  }
  res.fit.templeLengthLeft = extend(opts.hingeHalfWidth, res.fit.earLeft)
  res.fit.templeLengthRight = extend(-opts.hingeHalfWidth, res.fit.earRight)
  res.fit.headWidth = vec3.distance(res.fit.earLeft, res.fit.earRight)

  // IPD from iris centres if present (478-pt model), else eye corners midpoint
  const n = frame.landmarks.length / 3
  if (n >= 478) {
    landmarkToMetric(_p, frame.landmarks, FACE_LM.LEFT_IRIS_CENTER, camera, depthRef)
    landmarkToMetric(_tmp, frame.landmarks, FACE_LM.RIGHT_IRIS_CENTER, camera, depthRef)
    res.fit.ipd = vec3.distance(_p, _tmp)
  } else if (n >= 468) {
    const a: Vec3 = [0, 0, 0], b: Vec3 = [0, 0, 0]
    landmarkToMetric(a, frame.landmarks, FACE_LM.LEFT_EYE_INNER, camera, depthRef)
    landmarkToMetric(b, frame.landmarks, FACE_LM.LEFT_EYE_OUTER, camera, depthRef)
    vec3.lerp(_p, a, b, 0.5)
    landmarkToMetric(a, frame.landmarks, FACE_LM.RIGHT_EYE_INNER, camera, depthRef)
    landmarkToMetric(b, frame.landmarks, FACE_LM.RIGHT_EYE_OUTER, camera, depthRef)
    vec3.lerp(_tmp, a, b, 0.5)
    res.fit.ipd = vec3.distance(_p, _tmp)
  } else {
    res.fit.ipd = 0
  }
  return res
}
